import { Controller } from "@hotwired/stimulus"

/**
 * Button State Controller
 * 
 * Controls the enabled/disabled state of a button based on input field values.
 * 
 * Usage:
 *   <button data-controller="button-state"
 *           data-button-state-inputs-value='["#name", "#email", "input[name=gender]"]'
 *           data-button-state-mode-value="all"
 *           disabled>
 *     Submit
 *   </button>
 * 
 * Values:
 *   - inputs: JSON array of CSS selectors for inputs to watch
 *             If a selector matches multiple elements (e.g., radio group), 
 *             they are treated as one input (filled if ANY is filled)
 *   - mode: Logic mode for enabling the button:
 *       - "all" : Enable when ALL input groups are filled (default)
 *       - "any" : Enable when ANY input group is filled
 */
export default class extends Controller {
  static values = {
    inputs: Array,
    mode: { type: String, default: "all" }
  }

  connect() {
    this.boundUpdate = this.updateState.bind(this)
    this.inputGroups = []
    
    this.inputsValue.forEach(selector => {
      const elements = Array.from(document.querySelectorAll(selector))
      if (elements.length > 0) {
        this.inputGroups.push(elements)
        elements.forEach(el => {
          el.addEventListener("input", this.boundUpdate)
          el.addEventListener("change", this.boundUpdate)
        })
      }
    })
    
    // Initial state check
    this.updateState()
  }

  disconnect() {
    this.inputGroups.forEach(group => {
      group.forEach(el => {
        el.removeEventListener("input", this.boundUpdate)
        el.removeEventListener("change", this.boundUpdate)
      })
    })
  }

  updateState() {
    const filledCount = this.countFilledGroups()
    const totalCount = this.inputGroups.length
    const shouldEnable = this.evaluateCondition(filledCount, totalCount)
    
    this.element.disabled = !shouldEnable
  }

  countFilledGroups() {
    return this.inputGroups.filter(group => this.isGroupFilled(group)).length
  }

  isGroupFilled(group) {
    // A group is filled if ANY element in it is filled
    return group.some(el => this.isFilled(el))
  }

  isFilled(element) {
    if (element.type === "checkbox" || element.type === "radio") {
      return element.checked
    }
    
    if (element.type === "file") {
      return element.files && element.files.length > 0
    }
    
    if (element.tagName === "SELECT") {
      return element.value !== "" && element.value !== null
    }

    // Text inputs, textareas, etc.
    return element.value.trim() !== ""
  }

  evaluateCondition(filledCount, totalCount) {
    if (this.modeValue === "any") {
      return filledCount > 0
    }
    
    // Default: "all"
    return totalCount > 0 && filledCount === totalCount
  }
}
